import React, { FocusEventHandler, FormEventHandler, ReactElement, Ref, WeakValidationMap } from 'react';
import InteractiveListItem from '../InteractiveListItem';
import Checkbox from '../Checkbox';
import { forgeClassHelper } from '../utils/classes';
import PropTypes, { Validator } from 'prop-types';

const classes = forgeClassHelper('selection-list');

export interface SelectionListItemProps extends React.LiHTMLAttributes<HTMLElement> {
  // TODO-TYPES: should extend InteractiveListItem
  /** Aria-label to apply to the item */
  'aria-label'?: string;

  /** Content to render in SelectionItem */
  children: React.ReactNode;

  /** Adds a class to the root element of the component */
  className?: string;

  /** Impacts the styling of the checkbox in addition to preventing selection */
  disabled?: boolean;

  /** Highlights the checkbox when defined (specific string doesn't matter) */
  error?: string;

  /** Function that takes a JS event as an argument. Called when the SelectionList or SelectionListItem loses focus */
  onBlur?: FocusEventHandler<HTMLElement>;

  /** Function that takes a fake event as an argument. This event contains a target object with value and id properties. Called when the SelectionList or SelectionListItem value changes */
  onChange?: FormEventHandler<HTMLElement>;

  /**
   * Function that takes a JS event as an argument. Called when the SelectionList or SelectionListItem receives focus
   */
  onFocus?: FocusEventHandler<HTMLElement>;

  /** Determines whether the item is selected. */
  selected?: boolean;

  /** A unique identifier for this item in its list. Equivalent to a checkbox element's value attribute. */
  uniqueKey: string;

  /**
   * Sets the value attribute on the native `<input type="checkbox">`.
   * Note that SelectionListItem treats value differently than other inputs - the value does not change depending on the
   * status of `selected`.
   */
  value?: string;

  /** Ref forwarded to the input element */
  forwardedRef?: Ref<HTMLInputElement>;
}

/**
 * SelectionListItem
 *
 * A component to render an unordered list item.
 * @documentedBy SelectionList
 */
const SelectionListItem = ({
  children,
  uniqueKey,
  disabled = false,
  error,
  selected = false,
  'aria-label': ariaLabel,
  value,
  forwardedRef,
  ...rootProps
}: SelectionListItemProps): ReactElement => {
  const hasComplexChildren = children && typeof children !== 'string';
  const descriptionId = `${uniqueKey}-description`;
  const checkboxValue = value ? value : uniqueKey;
  return (
    <InteractiveListItem
      uniqueKey={uniqueKey}
      disabled={disabled}
      {...rootProps}
      {...classes({ extra: rootProps.className, element: 'item' })}
    >
      <Checkbox
        {...classes('checkbox')}
        value={checkboxValue}
        description={hasComplexChildren ? '\u200B' : children}
        disabled={disabled}
        error={error}
        id={uniqueKey}
        checked={selected}
        ref={forwardedRef}
        /* This is required to stop the onClick of the parent list being fired twice,
           once for the label and once for the checkbox itself */
        onClick={(event) => {
          event.stopPropagation();
        }}
        /* Setting checked makes this a controlled input
           controlled inputs should either have an onChange handler or be read-only
           in this case, the onChange handler is instantiated by the parent list,
           and so we stop propagation from the checkbox. */
        onChange={(event) => {
          event.stopPropagation();
        }}
        aria-label={ariaLabel}
        aria-labelledby={!ariaLabel && hasComplexChildren ? descriptionId : undefined}
      />
      {hasComplexChildren && (
        <span id={descriptionId} {...classes({ element: 'checkbox-description' })}>
          {children}
        </span>
      )}
    </InteractiveListItem>
  );
};

const selectionListItemPropTypes: WeakValidationMap<SelectionListItemProps & { '...rest': unknown }> = {
  /** A unique identifier for this item in its list. Equivalent to a checkbox element's value attribute. */
  uniqueKey: PropTypes.string.isRequired,
  /** Aria-label to apply to the item */
  'aria-label': PropTypes.string,
  /** Content to render in SelectionItem */
  children: PropTypes.node.isRequired,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Impacts the styling of the checkbox in addition to preventing selection */
  disabled: PropTypes.bool,
  /** Highlights the checkbox when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /**
   * Function that takes a JS event as an argument. Called when the SelectionList or SelectionListItem loses focus
   */
  onBlur: PropTypes.func,
  /**
   * Function that takes a fake event as an argument. This event contains a target object with value and id properties. Called when the SelectionList or SelectionListItem value changes
   */
  onChange: PropTypes.func,
  /**
   * Function that takes a JS event as an argument. Called when the SelectionList or SelectionListItem receives focus
   */
  onFocus: PropTypes.func,
  /** Ref forwarded to the input element */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    PropTypes.shape({
      /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
       *  correct, but causes a StackOverflow when building gatsby.
       */
      current: PropTypes.any,
    }).isRequired,
  ]) as Validator<Ref<HTMLInputElement>>,
  /** Determines whether the item is selected. */
  selected: PropTypes.bool,
  /**
   * Sets the value attribute on the native `<input type="checkbox">`.
   * Note that SelectionListItem treats value differently than other inputs - the value does not change depending on the
   * status of `selected`.
   */
  value: PropTypes.string,
  /**
   * Passthrough props to the `InteractiveListItem` implemented by the `SelectionListItem`
   */
  '...rest': PropTypes.any,
};
SelectionListItem.propTypes = selectionListItemPropTypes;

export default SelectionListItem;
